import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { RecruitmentActionType } from "../../../configs/constants/RecruitmentActionType";
import { RECRUITMENT_ACTION_KEY_VALUES } from "../../../configs/recruitmentActions.configs";
import { Recruitment } from "../../../models/Consultant";
import { KeyValue } from "../../../models/KeyValue";
import { useBulkUpdateCandidateRecruitmentDetails } from "../../../networking/candidate.hooks";
import AppDateTimePicker from "../../Core/AppDateTimePicker/AppDateTimePicker";
import AppText from "../../Core/AppText/AppText";
import Button from "../../Core/Button/Button";
import Dropdown from "../../Core/Dropdown/Dropdown";
import Modal from "../../Core/Modal/Modal";
import Flex from "../../Layout/Flex";

interface BulkRecruitmentActionModalProps {
	hideModal: Function;
	userIds: string[];
}

const BulkRecruitmentActionModal: React.FC<BulkRecruitmentActionModalProps> = ({
	hideModal,
	userIds,
}) => {
	const { t } = useTranslation();
	const { mutate: bulkUpdate, isLoading } = useBulkUpdateCandidateRecruitmentDetails();

	const [currentAction, setCurrentAction] = useState<RecruitmentActionType | undefined>(undefined);
	const [nextActionTime, setNextActionTime] = useState<Date | undefined>(undefined);

	const handleSubmit = async () => {
		const update: Partial<Recruitment> = {};
		if (currentAction) update.currentAction = currentAction;
		if (nextActionTime) update.nextActionTime = nextActionTime;

		if (Object.keys(update).length === 0) return;

		bulkUpdate({ userIds, update });
		hideModal();
	};

	const isSubmitDisabled = isLoading || (!currentAction && !nextActionTime);

	return (
		<Modal maxWidth="32rem" padding="1.375rem 3.125rem 3.125rem 3.125rem" overflowY="visible">
			<AppText size="xm" weight="bold" margin="0 0 1.25rem 0">
				{t("recruitment.bulk.updateAction")} ({userIds.length} {t("recruitment.bulk.recipients")})
			</AppText>

			<Flex direction="column" gap="1rem">
				<Flex direction="column">
					<AppText margin="0 0 0.25rem 0" weight="bold" color="grayTextDark">
						{t("CURRENT_ACTION")}
					</AppText>
					<Dropdown<KeyValue, RecruitmentActionType>
						onChange={(val) => setCurrentAction(val ? (val as RecruitmentActionType) : undefined)}
						getValue={(o) => o.key as RecruitmentActionType}
						value={
							currentAction ? { key: currentAction, value: currentAction } : { key: "", value: "" }
						}
						getOptionLabel={(option) => t(option.value as any)}
						options={RECRUITMENT_ACTION_KEY_VALUES}
						placeholder={t("CURRENT_ACTION")}
						width="100%"
					/>
				</Flex>

				<Flex direction="column">
					<AppText margin="0 0 0.25rem 0" weight="bold" color="grayTextDark">
						{t("NEXT_ACTION_DATE")}
					</AppText>
					<AppDateTimePicker
						onChange={setNextActionTime}
						placeholder={t("DATE_PICKER_PLACEHOLDER")}
						containerMargin="0"
						value={nextActionTime}
					/>
				</Flex>
			</Flex>

			<Flex direction="row" gap="0.75rem" margin="1.5rem 0 0 0" justifyContent="center">
				<Button
					disabled={isSubmitDisabled}
					loading={isLoading}
					width="150px"
					fontSize="xms"
					onClick={handleSubmit}
				>
					{t("SAVE")}
				</Button>
				<Button
					onClick={() => hideModal()}
					width="150px"
					textColor="black"
					backgroundColor="buttonGray"
					fontSize="s"
				>
					{t("SEND_MESSAGE_CANCEL")}
				</Button>
			</Flex>
		</Modal>
	);
};
export default BulkRecruitmentActionModal;
